import React from "react";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getYearOptions, MONTH_OPTIONS } from "./period";

interface ReportPeriodBarProps {
  /** Extra controls rendered inside the bar, after the summary. */
  children?: React.ReactNode;
  loading: boolean;
  month: string;
  /** e.g. "12 rows for June 2026" — shown after a successful run. */
  summary?: string;
  year: string;
  onMonthChange: (month: string) => void;
  onRun: () => void;
  onYearChange: (year: string) => void;
}

/** Month/year pickers + Run button shared by the scoreboard report pages. */
const ReportPeriodBar: React.FC<ReportPeriodBarProps> = ({
  children,
  loading,
  month,
  summary,
  year,
  onMonthChange,
  onRun,
  onYearChange,
}) => (
  <div className="flex flex-wrap items-end gap-4 rounded-md border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-500 dark:text-slate-400">
        Month
      </span>
      <Select onValueChange={onMonthChange} value={month}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Select month" />
        </SelectTrigger>
        <SelectContent>
          {MONTH_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-500 dark:text-slate-400">
        Year
      </span>
      <Select onValueChange={onYearChange} value={year}>
        <SelectTrigger className="w-[120px]">
          <SelectValue placeholder="Select year" />
        </SelectTrigger>
        <SelectContent>
          {getYearOptions().map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <Button disabled={loading} onClick={onRun}>
      <Play className="mr-1.5 h-4 w-4" />
      {loading ? "Running…" : "Run Report"}
    </Button>

    {summary && (
      <span className="text-sm text-gray-500 dark:text-slate-400">
        {summary}
      </span>
    )}

    {children}
  </div>
);

export default ReportPeriodBar;
